import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, ScanLine, Wallet, MoreHorizontal } from "lucide-react";

/**
 * useIsRadixDialogOpen — hide the BottomNav while a Radix Dialog is open.
 * The nav lives at z-[10000] (above shadcn Dialog's z-50) which was causing the
 * dialog's bottom rows + close X to be obscured on mobile. Mutation observer
 * watches `role=dialog[data-state=open]` elements anywhere in the DOM.
 */
function useIsRadixDialogOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => setOpen(!!document.querySelector('[role="dialog"][data-state="open"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "role"],
    });
    return () => obs.disconnect();
  }, []);
  return open;
}

/**
 * BottomNav — mobile-only bottom navigation bar.
 *
 * Iter-64: rendered on screens < md (768px) only. Above that breakpoint the legacy sidebar
 * is shown. The nav exposes the 4 primary flows requested by the user:
 *   1. Dashboard
 *   2. Timesheet
 *   3. Missions
 *   4. CRA Validation (managers/CEO) OR Notifications (other roles)
 * + a "More" button that opens a drawer with the rest of the menu (settings, billing, etc.).
 *
 * The bar is fixed at the bottom of the viewport, ~60px tall, with safe-area padding for
 * iPhone home-indicator. Active item gets the brand-navy background pill.
 */
const Pill = ({ to, icon: Icon, label, badge, testid, isMore = false, onClick }) => {
  const location = useLocation();
  const active = !isMore && (location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to)));
  const Inner = (
    <>
      <div className="relative">
        <Icon size={20} strokeWidth={1.75} className={active ? "text-white" : "text-brand-navy"} />
        {badge ? (
          <span
            data-testid={`${testid}-badge`}
            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center text-[9px] font-bold rounded-full bg-brand-orange text-white"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </div>
      <span className="text-[10px] mt-1 font-medium leading-none">{label}</span>
    </>
  );
  const cls = `flex-1 flex flex-col items-center justify-center py-1.5 transition-colors ${
    active
      ? "text-white bg-brand-navy"
      : "text-brand-ink/80 hover:bg-brand-orange-100"
  }`;
  if (isMore) {
    return (
      <button onClick={onClick} data-testid={testid} className={cls}>
        {Inner}
      </button>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {Inner}
    </Link>
  );
};

export default function BottomNav({ user, badges, onMore }) {
  const dialogOpen = useIsRadixDialogOpen();
  if (!user) return null;
  if (dialogOpen) return null;
  // Iter-228 — gate mobile pills via permissions, not raw role strings.
  const perms = user.permissions || [];
  const canTimesheet = perms.includes("timesheet");
  const canSubmitExpenses = perms.includes("submit_expenses");
  const canSeeCabinet = perms.includes("manage_cabinet_expenses");
  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-[10000] bg-[#FAF6EE] border-t border-brand-navy/15 flex items-stretch md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <Pill to="/dashboard" icon={LayoutDashboard} label="Home" testid="mnav-dashboard" />
      {canTimesheet && (
        <Pill to="/timesheet" icon={CalendarDays} label="Timesheet" testid="mnav-timesheet" />
      )}
      {canSubmitExpenses && (
        <Pill to="/expenses" icon={ScanLine} label="Notes de frais" testid="mnav-expenses" />
      )}
      {canSeeCabinet && (
        <Pill to="/cabinet-expenses" icon={Wallet} label="Cabinet" testid="mnav-cabinet-expenses" />
      )}
      <Pill icon={MoreHorizontal} label="More" testid="mnav-more" isMore onClick={onMore} />
    </nav>
  );
}
